import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Center, DataList, Divider, EmptyState, Group, Highlight, Text, Typography } from "@mantine/core";
import { Spotlight, type SpotlightActionData } from "@mantine/spotlight";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SearchType } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import useSearch from "src/hooks/data/load/useSearch";
import ResponseStatusAlert from "./ResponseStatusAlert";
import InlineLoading from "./loading/InlineLoading";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useDebouncedValue } from "@mantine/hooks";

type TMDSearchActionData =
    {
        to: string;
        title: string;
        query: string;
        description: string;
        icon: IconDefinition;
        details?: Array<{
            label?: string;
            value?: string;
        }>;
    }

const TMDSpotlightAction: FC<TMDSearchActionData> = (
    {
        to,
        title,
        query,
        description,
        icon,
        details
    }
) => {
    const navigate = useNavigate();

    const detailsComponents = useMemo(() => {
        if (
            !details ||
            details.length === 0
        ) {
            return undefined;
        }

        const filtered = details.filter(x => x.label && x.value);

        if (filtered.length === 0) {
            return undefined;
        }

        return (
            <DataList mt="sm">
                {
                    filtered.map((item, index) => {
                        return (
                            <DataList.Item key={index}>
                                <DataList.ItemLabel w={160} maw={160} miw={160}>
                                    {item.label}
                                </DataList.ItemLabel>
                                <DataList.ItemValue 
                                    component={Highlight}
                                    // @ts-ignore
                                    highlight={query}
                                    style={{
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                        {item.value}
                                </DataList.ItemValue>
                            </DataList.Item>
                        )
                    })
                }
            </DataList>
        )
    }, [details, query])

    return (
        <>
            <Spotlight.Action 
                component={Link}
                // @ts-ignore
                to={to}

                onClick={() => {
                    navigate(to);
                }}
                closeSpotlightOnTrigger={true}
                onTrigger
                highlightQuery
                highlightColor="secondary.6"
            >
                <Group 
                    wrap="nowrap" 
                    w="100%"
                    align="top"
                    py="xs"
                >
                    <FontAwesomeIcon icon={icon} size="lg" />
                    <div style={{ flex: 1 }}>
                        <Text>
                            <Highlight highlight={query}>
                                {title}
                            </Highlight>
                        </Text>

                        {!detailsComponents && description && (
                            <Text opacity={0.6} size="xs">
                                <Highlight highlight={query}>
                                    {description}
                                </Highlight>
                            </Text>
                        )}
                        {detailsComponents}
                    </div>

                    {/* {item.new && <Badge variant="default">new</Badge>} */}
                </Group>
            </Spotlight.Action>
            <Divider />
        </>
    )
}

const Search = () => {
    const [query, setQuery] = useState('');

    const [debouncedQuery] = useDebouncedValue(query, 500)

    const { data, loading, error } = useSearch(debouncedQuery);
    
    const searchResults = useMemo(() => {

        const actions: ReactNode[] = [];

        if (!data) {
            return actions;
        }

        let unknownCount = 0;

        for (let result of data) {

            let action: TMDSearchActionData & { key: string };

            switch (result.searchType) {
                case SearchType.Trial:
                    action = {
                        key: `Trial-${result.trialId}`,
                        title: result.title,
                        description: result.description,
                        to: `/trials/${result.trialId}/details`,
                        icon: AppSettings.icons.trial,
                        query: debouncedQuery,
                        details: [
                            {
                                label: 'TROG Number',
                                value: result.details?.trialDisplayNumber
                            },
                            {
                                label: 'Title',
                                value: result.details?.trialTitle
                            },
                            {
                                label: 'Status',
                                value: result.details?.trialStatus
                            },
                            {
                                label: 'Category',
                                value: result.details?.trialCategory
                            }
                        ]
                    }
                    break;
                case SearchType.Group:
                    action = {
                        key: `Group-${result.groupId}`,
                        title: result.title,
                        description: result.description,
                        to: `/groups/${result.groupId}/details`,
                        icon: AppSettings.icons.group,
                        query: debouncedQuery,
                        details: [
                            {
                                label: 'Code',
                                value: result.details?.groupCode
                            },
                            {
                                label: 'Type',
                                value: result.details?.groupType
                            },
                            {
                                label: 'Suburb',
                                value: result.details?.groupSuburb
                            },
                            {
                                label: 'State',
                                value: result.details?.groupState
                            },
                            {
                                label: 'Country',
                                value: result.details?.groupCountry
                            }
                        ]
                    };
                    break;
                case SearchType.Institution:
                    action = {
                        key: `Institutions-${result.institutionId}`,
                        title: result.title,
                        description: result.description,
                        to: `/institutions/${result.institutionId}/details`,
                        icon: AppSettings.icons.institution,
                        query: debouncedQuery,
                        details: [
                            {
                                label: 'Code',
                                value: result.details?.institutionCode
                            },
                            {
                                label: 'Type',
                                value: result.details?.institutionType
                            },
                            {
                                label: 'Suburb',
                                value: result.details?.institutionSuburb
                            },
                            {
                                label: 'State',
                                value: result.details?.institutionState
                            },
                            {
                                label: 'Country',
                                value: result.details?.institutionCountry
                            }
                        ]
                    };
                    break;
                case SearchType.Patient:
                    action = {
                        key: `Patient-${result.patientId}`,
                        title: result.title,
                        description: result.description,
                        to: `/trials/${result.trialId}/institutions/${result.trialInstitutionId}/participants/${result.patientId}`,
                        icon: AppSettings.icons.participant,
                        query: debouncedQuery,
                        details: [
                            {
                                label: 'Trial',
                                value: result.details?.trialDisplayNumber
                            },
                            {
                                label: 'Initials',
                                value: result.details?.patientInitials
                            },
                            {
                                label: 'Institution',
                                value: result.details?.institutionName
                            },
                            {
                                label: 'Treatment',
                                value: result.details?.trialTreatmentName
                            }
                        ]
                    };
                    break;
                case SearchType.Personnel:
                    action = {
                        key: `Personnel-${result.personnelId}`,
                        title: result.title,
                        description: result.description,
                        to: `/personnel/${result.personnelId}/details`,
                        icon: AppSettings.icons.user,
                        query: debouncedQuery,
                        details: [
                            {
                                label: 'Username',
                                value: result.details?.personnelUserName
                            },
                            {
                                label: 'Membership Type',
                                value: result.details?.personnelMembershipType
                            },
                            {
                                label: 'Professional Discipline',
                                value: result.details?.personnelProfessionalDiscipline
                            }
                        ]
                    };
                    break;
                default:
                    unknownCount++;
                    action = {
                        key: `Unknown-${unknownCount}`,
                        title: result.title,
                        description: result.description,
                        to: `/`,
                        icon: AppSettings.icons.unknown,
                        query: debouncedQuery
                    };
                    break;
            }


            actions.push(
                <TMDSpotlightAction
                    {...action}
                />
            );
        }

        return actions;
    }, [data])

    return (
        <Spotlight.Root 
            query={query} 
            onQueryChange={setQuery} 
            closeOnActionTrigger={true}
            
        >
            <Spotlight.Search 
                placeholder="Search..." 
                leftSection={<FontAwesomeIcon icon={AppSettings.icons.search} />} 
                rightSection={debouncedQuery && <Text>{searchResults.length} Results</Text>}
                rightSectionWidth={150}
                rightSectionProps={{
                    style: {
                        textAlign: 'right'
                    }
                }}
            />
            <Spotlight.ActionsList>
                {loading && <InlineLoading height={200} text="Searching..." />}
                {error && !loading && <ResponseStatusAlert responseStatus={error} />}
                {
                    !error && searchResults.length === 0 && !query && !loading && (
                        <Spotlight.Empty>
                            <EmptyState
                                icon={<FontAwesomeIcon icon={AppSettings.icons.search} />}
                                title="Begin Searching"
                                description={
                                    <Typography>
                                        Enter keywords to begin searching for Trials, Institutions, Groups, Personnel or Participants.
                                    </Typography>
                                }
                            />
                        </Spotlight.Empty>
                    )
                }
                {
                    !error && searchResults.length === 0 && query && !loading && (
                        <Spotlight.Empty>
                            <EmptyState
                                icon={<FontAwesomeIcon icon={AppSettings.icons.search} />}
                                title="No results found"
                                description={
                                    <Typography>
                                        We couldn't find anything matching your search.
                                        Try searching with different keywords to see more results.
                                    </Typography>
                                }
                            />
                        </Spotlight.Empty>
                    )
                }
                {
                    !error && searchResults.length !== 0 && !loading && (
                        searchResults
                    )
                }
            </Spotlight.ActionsList>
        </Spotlight.Root>
    );
};

export default Search;
